import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, User, LogOut, Search, Heart, LayoutDashboard, Shield, Calendar, ChevronDown, Users, Music, Disc3, Mic2, ShoppingBag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { useAuth } from '../context/AuthContext';
import CowboyBoot from './icons/CowboyBoot';

import { API_URL } from '../utils/api';

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hasDJs, setHasDJs] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Check if there are any DJs registered
  useEffect(() => {
    const checkDJs = async () => {
      try {
        const response = await fetch(`${API_URL}/api/djs`);
        if (response.ok) {
          const djs = await response.json();
          setHasDJs(djs.length > 0);
        }
      } catch (error) {
        console.error('Error checking DJs:', error);
      }
    };
    checkDJs();
  }, []);

  // Check if admin is impersonating
  useEffect(() => {
    setIsImpersonating(!!localStorage.getItem('admin_token'));
  }, [location]);

  const handleReturnToAdmin = () => {
    const adminToken = localStorage.getItem('admin_token');
    if (adminToken) {
      localStorage.setItem('token', adminToken);
      localStorage.removeItem('admin_token');
      window.location.href = '/admin';
    }
  };

  const handleLogout = async () => {
    localStorage.removeItem('admin_token');
    await logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;
  const isDirectoryActive = () => ['/clubs', '/instructors', '/djs', '/artists', '/classes'].includes(location.pathname);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/events', label: 'Events' },
    { path: '/weekends', label: 'Weekends' },
    { path: '/shop', label: 'Shop' },
    { path: '/blog', label: 'Blog' },
    { path: '/about-us', label: 'About Us' },
    { path: '/dances/popular', label: 'Top Dances' },
  ];

  const directoryLinks = [
    { path: '/clubs', label: 'Clubs', icon: Music },
    { path: '/instructors', label: 'Instructors', icon: Users },
    { path: '/djs', label: 'DJs', icon: Disc3 },
    { path: '/artists', label: 'Artists', icon: Mic2 },
    { path: '/classes', label: 'Classes', icon: Calendar },
  ];

  const visibleNavLinks = navLinks.filter(link => link.showIf === undefined || link.showIf);

  return (
    <>
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="bg-amber-500 text-amber-950 px-4 py-2 text-center text-sm font-medium flex items-center justify-center gap-3">
          <span>You are viewing as another user</span>
          <button
            onClick={handleReturnToAdmin}
            className="bg-amber-700 text-white px-3 py-1 rounded-full text-xs font-semibold hover:bg-amber-800 transition-colors"
          >
            Return to Admin
          </button>
        </div>
      )}
      
      <nav className="bg-white/95 backdrop-blur-sm border-b border-stone-200 sticky top-0 z-50" data-testid="navbar">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-18">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group" data-testid="navbar-logo">
              <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_dancer-community/artifacts/buaegm7r_1D08A5CF-84D3-4ADA-9A3D-0F8EBE77862A.png" 
                  alt="Line Dance Central Logo"
                  className="w-14 h-14 sm:w-16 sm:h-16 object-contain"
                />
              </div>
              <div className="hidden sm:flex flex-col items-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <span className="text-xl sm:text-2xl font-extrabold text-stone-800 leading-tight">
                  Line Dance
                </span>
                <span className="text-lg sm:text-xl font-extrabold text-violet-600 leading-tight -mt-1">
                  Central
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden xl:flex items-center gap-6">
              {/* Home Link */}
              <Link
                to="/"
                className={`font-medium transition-colors ${
                  isActive('/')
                    ? 'text-violet-600'
                    : 'text-stone-600 hover:text-stone-900'
                }`}
                data-testid="nav-home"
              >
                Home
              </Link>

              {/* Directory Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={`font-medium transition-colors flex items-center gap-1 ${
                      isDirectoryActive()
                        ? 'text-violet-600'
                        : 'text-stone-600 hover:text-stone-900'
                    }`}
                    data-testid="nav-directory"
                  >
                    Directory
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48 bg-white border-stone-200">
                  {directoryLinks.map(link => (
                    <DropdownMenuItem 
                      key={link.path}
                      onClick={() => navigate(link.path)} 
                      className={`text-stone-700 focus:bg-violet-50 focus:text-violet-700 ${
                        isActive(link.path) ? 'bg-violet-50 text-violet-600' : ''
                      }`}
                      data-testid={`nav-${link.label.toLowerCase()}`}
                    >
                      <link.icon className="w-4 h-4 mr-2" />
                      {link.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Other Nav Links */}
              {visibleNavLinks.filter(link => link.path !== '/').map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-medium transition-colors ${
                    isActive(link.path)
                      ? 'text-violet-600'
                      : 'text-stone-600 hover:text-stone-900'
                  }`}
                  data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-3">
              <Link to="/clubs">
                <Button variant="ghost" size="icon" className="hidden sm:flex text-stone-600 hover:text-stone-900 hover:bg-stone-100" data-testid="search-btn">
                  <Search className="w-5 h-5" />
                </Button>
              </Link>

              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2 px-2 hover:bg-stone-100" data-testid="user-menu-btn">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={user.picture} />
                        <AvatarFallback className="bg-violet-100 text-violet-600 font-bold text-sm">
                          {user.name?.split(' ').map(n => n[0]).join('') || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <span className="hidden sm:block font-medium text-stone-700">{user.name?.split(' ')[0]}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-white border-stone-200">
                    <div className="px-3 py-2">
                      <p className="font-semibold text-stone-900">{user.name}</p>
                      <p className="text-sm text-stone-500 truncate">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator className="bg-stone-200" />
                    <DropdownMenuItem onClick={() => navigate('/dashboard')} className="text-stone-700 focus:bg-violet-50 focus:text-violet-700" data-testid="dropdown-dashboard">
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/dashboard?tab=favorites')} className="text-stone-700 focus:bg-violet-50 focus:text-violet-700" data-testid="dropdown-favorites">
                      <Heart className="w-4 h-4 mr-2" />
                      My Favorites
                    </DropdownMenuItem>
                    {user?.is_admin && (
                      <DropdownMenuItem onClick={() => navigate('/admin')} className="text-stone-700 focus:bg-violet-50 focus:text-violet-700" data-testid="dropdown-admin">
                        <Shield className="w-4 h-4 mr-2" />
                        Admin Panel
                      </DropdownMenuItem>
                    )}
                    {user?.role === 'booking_manager' && !user?.is_admin && (
                      <DropdownMenuItem onClick={() => navigate('/admin')} className="text-stone-700 focus:bg-violet-50 focus:text-violet-700" data-testid="dropdown-bookings">
                        <Calendar className="w-4 h-4 mr-2" />
                        Manage Bookings
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator className="bg-stone-200" />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:bg-red-50 focus:text-red-700" data-testid="dropdown-logout">
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="flex items-center gap-2">
                  <Link to="/login">
                    <Button variant="ghost" className="hidden sm:flex text-stone-600 hover:text-stone-900 hover:bg-stone-100" data-testid="login-btn">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-6" data-testid="register-btn">
                      Get Started
                    </Button>
                  </Link>
                </div>
              )}

              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                className="xl:hidden text-stone-600 hover:bg-stone-100"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </Button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="xl:hidden py-4 border-t border-stone-200">
              <div className="flex flex-col gap-2">
                {/* Home */}
                <Link
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-3 rounded-xl font-medium transition-colors ${
                    isActive('/')
                      ? 'bg-violet-50 text-violet-600'
                      : 'text-stone-600 hover:bg-stone-50'
                  }`}
                >
                  Home
                </Link>

                {/* Directory Section */}
                <div className="px-4 py-2">
                  <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2">Directory</p>
                  <div className="flex flex-col gap-1 ml-2">
                    {directoryLinks.map(link => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`px-3 py-3 rounded-xl font-medium flex items-center gap-2 transition-colors ${
                          isActive(link.path)
                            ? 'bg-violet-50 text-violet-600'
                            : 'text-stone-600 hover:bg-stone-50'
                        }`}
                      >
                        <link.icon className="w-4 h-4" />
                        {link.label}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Other Links */}
                {visibleNavLinks.filter(link => link.path !== '/').map(link => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`px-4 py-3 rounded-xl font-medium transition-colors ${
                      isActive(link.path)
                        ? 'bg-violet-50 text-violet-600'
                        : 'text-stone-600 hover:bg-stone-50'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                
                {user && (
                  <>
                    <Link
                      to="/dashboard"
                      onClick={() => setMobileMenuOpen(false)}
                      className={`px-4 py-3 rounded-xl font-medium transition-colors ${
                        isActive('/dashboard')
                          ? 'bg-violet-50 text-violet-600'
                          : 'text-stone-600 hover:bg-stone-50'
                      }`}
                    >
                      Dashboard
                    </Link>
                    {user?.is_admin && (
                      <Link
                        to="/admin"
                        onClick={() => setMobileMenuOpen(false)}
                        className={`px-4 py-3 rounded-xl font-medium flex items-center transition-colors ${
                          isActive('/admin')
                            ? 'bg-violet-50 text-violet-600'
                            : 'text-stone-600 hover:bg-stone-50'
                        }`}
                      >
                        <Shield className="w-4 h-4 mr-2" />
                        Admin Panel
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        handleLogout();
                        setMobileMenuOpen(false);
                      }}
                      className="px-4 py-3 rounded-xl font-medium text-left text-red-600 hover:bg-red-50 transition-colors"
                    >
                      Sign Out
                    </button>
                  </>
                )}
                {!user && (
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-4 py-3 rounded-xl font-medium text-stone-600 hover:bg-stone-50 transition-colors"
                  >
                    Sign In
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
}
